import { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { css } from '@emotion/react';
import Colors from '../../../types/colors';
import MESSAGES from '../../../constants/messages';
import { ImageEmptyGraph } from '../../../assets/images/svg';
import useDpi from '../../../hooks/useDpi';
import { IDashboardDataListItem, IGraphDataItem } from '../../../apis/v1/dashboard.api';
import { IChartLegendItem } from '../../molecules/common/bar-chart-legend/const';

interface NewBarChartProps {
  BarData?: IDashboardDataListItem;
  currentLegendList?: IChartLegendItem[];
}

function NewBarChart({ BarData, currentLegendList }: NewBarChartProps) {
  const { subFontSize } = useDpi();
  const chartRef = useRef(null);
  const [yAxisLabels, setYAxisLabels] = useState<{ value: number; y: number }[]>([]);

  const maxVal = BarData?.maxValue || 1;
  const legendLineInfo = BarData?.legendLineInfo || [0, 1];
  const graphData = BarData?.graph || [];
  const uniqueNthYears = new Set(graphData.map((item: { nthYear: number }) => item.nthYear));
  const defaultLabels = Array.from({ length: uniqueNthYears.size }, (_, index) => {
    const currentItem = graphData.find((d: { nthYear: number }) => d.nthYear === index + 1);
    const alertAsterisk = BarData?.alertYn ? '*' : '';
    return `${currentItem ? `${currentItem.nthYear}차` : `${index + 1}차`}${alertAsterisk}`;
  });

  const defaultData = Array.from({ length: uniqueNthYears.size }, (_, index) => {
    const currentItem = graphData[index];
    if (currentItem) {
      const value = parseFloat(currentItem?.value?.toFixed(1));
      return value !== 0 ? value : null;
    }
    return '';
  });
  const MAX_DATASETS = 3;
  const isDataEmpty = defaultData.every((value) => value === '');
  const stepCase = maxVal === 3 && legendLineInfo.length === 2;

  const separatedData = (graphData || []).reduce<Record<number, IGraphDataItem[]>>((acc, current) => {
    const year = current.nthYear;
    if (!acc[year]) acc[year] = [];
    acc[year].push(current);
    return acc;
  }, {});

  const yearGroups = Object.values(separatedData);

  // console.group('bar-chart');
  // console.log('separatedData', separatedData);
  // console.log('yearGroups', yearGroups);
  // console.groupEnd();

  const maxItemCount = Math.min(Math.max(...yearGroups.map((group) => group.length), 0), MAX_DATASETS);

  const datasets: { values: (number | null | string)[]; colors: string[] }[] = Array.from({ length: maxItemCount }, () => ({ values: [], colors: [] }));

  yearGroups.forEach((group) => {
    for (let d = 0; d < maxItemCount; d++) {
      if (d < group.length) {
        datasets[d].values.push(group[d].value);
        const currentColor = currentLegendList?.find((item) => item.range?.(group[d].value))?.color;
        datasets[d].colors.push(currentColor || '');
      } else {
        datasets[d].values.push('');
        datasets[d].colors.push('');
      }
    }
  });

  const max = maxVal * 1.1;

  const barData = datasets
    .filter((ds) => ds.values.some((v) => typeof v === 'number'))
    .map((ds, i) => ({
      originalData: ds.values,
      // data: dotGraphCase ? ds.values.map((v) => (typeof v === 'number' ? 0.3 : null)) : ds.values.map((v) => (typeof v === 'number' && v !== 0 ? Math.min(v as number, maxVal) : null)),
      // backgroundColor: dotGraphCase ? ds.colors.map(() => 'transparent') : ds.colors,
      data: ds.values.map((v) => (typeof v === 'number' ? Math.min(v as number, maxVal) : null)),
      backgroundColor: ds.colors,
      barThickness: 24,
      categoryPercentage: 0.5,
      borderRadius: 4,
    }));

  useEffect(() => {
    const data = {
      labels: defaultLabels,
      datasets: barData,
    };
    const horizontalDottedLineMain = {
      id: 'horizontalDottedLineMain',
      beforeDatasetsDraw(chart: Chart, args: { cancelable: true }, options: any): boolean | void {
        const {
          ctx,
          chartArea: { left, width },
          scales: { y },
        } = chart;

        ctx.save();
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 0.2;
        ctx.setLineDash([1.5, 3]);

        if (!BarData) return;
        if (!isDataEmpty) {
          legendLineInfo.forEach((lineValue, index) => {
            if (stepCase && index !== 0) return;
            const eachMaxLineY = y.getPixelForValue(lineValue);
            ctx.strokeRect(left, eachMaxLineY, width - 24, 0);
          });
        }
        ctx.restore();
      },
    };

    // no data plugin
    const noData = {
      id: 'noData',
      afterDatasetsDraw: (chart: { ctx: any; data: any; getDatasetMeta?: any; chartArea: { top: any; bottom: any; left: any; right: any; width: any; height: any } }, args: any, plugins: any) => {
        const {
          ctx,
          chartArea: { top, left, width, height },
        } = chart;

        ctx.save();

        chart.data.datasets.forEach((dataset: { data: { [x: string]: any } }) => {
          const meta = chart.getDatasetMeta(0);
          const bars = meta.data;

          // Loop through each bar
          bars.forEach((bar: { x: any }, index: string | number) => {
            // const yPosition = y.getPixelForValue(datasets[0].data[index]) - 20;
            if (isDataEmpty) {
              ctx.font = `${subFontSize}px`;
              ctx.textAlign = 'center';
              ctx.fillStyle = `${Colors['Grey_05_subtext']}`;
              const img = new Image();
              img.src = ImageEmptyGraph;
              img.onload = () => {
                const imgWidth = 45;
                const imgHeight = 45;
                const imgX = left + width / 2 - imgWidth / 2; // center horizontally
                const imgY = top + height / 3; // 1/3 of the way down vertically
                ctx.drawImage(img, imgX, imgY, imgWidth, imgHeight);

                // const textTopY = imgY + imgHeight + 15; // 10px padding
                const textTopY = imgY + imgHeight + 15; // 10px padding
                const textBottomY = textTopY + 16; // 16px line height
                const textX = left + width / 2; // center horizontally
                ctx.fillText(`${MESSAGES.INFORMATION.EMPTY_GRAPH_DATA.TOP}`, textX, textTopY, 150);
                ctx.fillText(`${MESSAGES.INFORMATION.EMPTY_GRAPH_DATA.TOP}`, textX, textBottomY, 150);
              };
            }
          });
        });
      },
    };

    // config
    const config = {
      type: 'bar',
      data,
      options: {
        scales: {
          y: {
            beginAtZero: legendLineInfo[0] === 0,
            min: legendLineInfo[0],
            max: max,
            grace: 0,
            gridLines: {
              display: false,
            },
            afterTickToLabelConversion: (ctx: { fillText: any; ticks: { value: number; label: string; color: string }[] }) => {
              ctx.ticks = [];
              if (isDataEmpty) {
                ctx.ticks = [];
              }
            },

            grid: {
              display: false,
              drawTicks: false,
              drawBorder: false,
            },
            border: {
              display: false,
            },
          },
          x: {
            beginAtZero: true,
            ticks: {
              display: true,
              color: (context: any, index: any) => {
                const tickColor: string[] = [];
                context.chart.data.datasets[0].data.forEach((datapoint: string, index: any) => {
                  if (datapoint === '') {
                    tickColor.push('#aaaaac');
                  } else {
                    tickColor.push('black');
                  }
                });

                return tickColor;
              },
              padding: 8,
            },

            grid: {
              display: false,
              drawBorder: false,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            yAlign: 'bottom',
          },
          datalabels: {
            display: !stepCase,
            anchor: 'end',
            align: 'top',
            color: 'black',
            font: {
              size: `${subFontSize}`,
              weight: 'bold',
            },
            formatter: (_value: number, context: any) => {
              const original = context.dataset.originalData?.[context.dataIndex];
              if (original == null || original === '') return null;
              return typeof original === 'number' ? Math.round(original) : original;
            },
          },
        },

        layout: {
          padding: {
            top: 7,
            right: 20,
            left: 0,
          },
        },
        responsive: true,
        maintainAspectRatio: false,
        chartArea: {
          height: '100%',
          width: '100%',
        },
      },

      plugins: [ChartDataLabels, horizontalDottedLineMain, noData],
    };

    // render init block
    // @ts-ignore
    const myChart = new Chart(chartRef.current, config);

    if (myChart.scales?.y && !isDataEmpty) {
      const positions = legendLineInfo.map((value) => ({
        value,
        y: myChart.scales.y.getPixelForValue(value),
      }));
      setYAxisLabels(positions);
    } else {
      setYAxisLabels([]);
    }

    // Instantly assign Chart.js version
    const chartVersion = document.getElementById('chartVersion');
    // @ts-ignore
    chartVersion.innerText = Chart.version;

    return () => {
      myChart.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BarData]);

  const wrapper = css`
    //width: 360px;
    //height: 226px;
    position: relative;
    margin-top: 10px;
    font-size: ${subFontSize}px;
  `;

  const NewBarChartWrapper = css`
    flex: 1;
    height: 100%;
    overflow-x: auto;
    min-width: 0;
  `;

  const NewBarChart = css`
    position: relative;
    width: ${defaultLabels.length > 5 ? `${300 + (defaultLabels.length - 5) * 50}px` : '100%'};
    height: 100%;

    @media only screen and (max-width: 1280px) {
      width: ${defaultLabels.length > 5 ? `${1100 + (defaultLabels.length - 5) * 50}px` : '100%'};
    }

    @media only screen and (max-width: 1024px) {
      width: ${defaultLabels.length > 5 ? `${1000 + (defaultLabels.length - 5) * 50}px` : '100%'};
    }
    @media only screen and (max-width: 912px) {
      width: ${defaultLabels.length > 5 ? `${880 + (defaultLabels.length - 5) * 50}px` : '100%'};
    }
    @media only screen and (max-width: 820px) {
      width: ${defaultLabels.length > 5 ? `${780 + (defaultLabels.length - 5) * 50}px` : '100%'};
    }

    @media only screen and (max-width: 768px) {
      width: ${defaultLabels.length > 5 ? `${750 + (defaultLabels.length - 5) * 50}px` : '100%'};
    }
    @media only screen and (max-width: 540px) {
      width: ${defaultLabels.length > 5 ? `${500 + (defaultLabels.length - 5) * 50}px` : '100%'};
    }
    @media only screen and (max-width: 414px) {
      width: ${defaultLabels.length > 5 ? `${374 + (defaultLabels.length - 5) * 50}px` : '100%'};
    }
    @media only screen and (max-width: 412px) {
      width: ${defaultLabels.length > 5 ? `${370 + (defaultLabels.length - 5) * 50}px` : '100%'};
    }
    @media only screen and (max-width: 393px) {
      width: ${defaultLabels.length > 5 ? `${345 + (defaultLabels.length - 5) * 50}px` : '100%'};
    }
    @media only screen and (max-width: 375px) {
      width: ${defaultLabels.length > 5 ? `${345 + (defaultLabels.length - 5) * 50}px` : '100%'};
    }
    @media only screen and (max-width: 360px) {
      width: ${defaultLabels.length > 5 ? `${340 + (defaultLabels.length - 5) * 50}px` : '100%'};
    }

    @media only screen and (max-width: 280px) {
      width: ${defaultLabels.length > 5 ? `${260 + (defaultLabels.length - 5) * 50}px` : '100%'};
    }
  `;
  const ySales = css`
    position: relative;
    left: 12px;
    top: 0px;
    color: #aaaaac;
    font-size: 11px;
  `;

  const chartRow = css`
    display: flex;
    height: 226px;
    @media only screen and (max-width: 1280px) {
      height: 440px;
    }
    @media only screen and (max-width: 1024px) {
      height: 390px;
    }
    @media only screen and (max-width: 912px) {
      height: 390px;
    }
    @media only screen and (max-width: 820px) {
      height: 360px;
    }
    @media only screen and (max-width: 768px) {
      height: 330px;
    }
    @media only screen and (max-width: 540px) {
      height: 300px;
    }
    @media only screen and (max-width: 414px) {
      height: 250px;
    }
    @media only screen and (max-width: 412px) {
      height: 250px;
    }
    @media only screen and (max-width: 393px) {
      height: 220px;
    }
    @media only screen and (max-width: 375px) {
      height: 210px;
    }
    @media only screen and (max-width: 360px) {
      height: 210px;
    }
    @media only screen and (max-width: 280px) {
      height: 200px;
    }
  `;

  const yAxisContainer = css`
    flex-shrink: 0;
    width: 40px;
    height: 100%;
    position: relative;
    z-index: 2;
    pointer-events: none;
  `;

  const yAxisLabel = css`
    position: absolute;
    right: 4px;
    color: #ababab;
    font-size: 10px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
    transform: translateY(-50%);
    white-space: nowrap;
  `;

  const xAxisUnit = css`
    position: absolute;
    bottom: 8px;
    right: 0px;
    color: #aaaaac;
    font-size: 11px;
  `;

  return (
    <div css={wrapper}>
      <div css={ySales}>(점수)</div>
      <span id="chartVersion" style={{ display: 'none' }}></span>
      <div css={chartRow}>
        <div css={yAxisContainer}>
          {yAxisLabels.length > 0 && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: `${Math.min(...yAxisLabels.map((l) => l.y))}px`,
                height: `${Math.max(...yAxisLabels.map((l) => l.y)) - Math.min(...yAxisLabels.map((l) => l.y))}px`,
                width: '1px',
                backgroundColor: '#ababab',
              }}
            />
          )}
          {!stepCase &&
            yAxisLabels.map(({ value, y }) => (
              <span key={value} css={yAxisLabel} style={{ top: `${y}px` }}>
                {value}
              </span>
            ))}
        </div>
        <div css={NewBarChartWrapper}>
          <div css={NewBarChart}>
            <canvas ref={chartRef}></canvas>
            <span css={xAxisUnit}>(차수)</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewBarChart;

// const dotGraphCase = maxVal === 1 && legendLineInfo.length === 2;

// const dotColors = dotGraphCase ? datasets.filter((ds) => ds.values.some((v) => typeof v === 'number')).map((ds) => [...ds.colors]) : [];

// const dotPlugin = {
//   id: 'dotPlugin',
//   afterDatasetsDraw(chart: Chart) {
//     if (!dotGraphCase) return;
//     const { ctx, chartArea } = chart;
//     chart.data.datasets.forEach((_dataset: any, dsIndex: number) => {
//       const meta = chart.getDatasetMeta(dsIndex);
//       meta.data.forEach((bar: any, index: number) => {
//         const color = dotColors[dsIndex]?.[index];
//         if (!color || color === '') return;
//         ctx.save();
//         ctx.beginPath();
//         ctx.arc(bar.x, (chartArea.top + chartArea.bottom) / 2, 20, 0, Math.PI * 2);
//         ctx.fillStyle = color;
//         ctx.fill();
//         ctx.restore();
//       });
//     });
//   },
// };
